import { useRef, useState } from 'react'
import { ActivityIndicator, Dimensions } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigation } from '@react-navigation/native'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import { useToast } from '@zet/common/components/toast'
import { Icon } from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import { BottomSheet } from '@zet/design-system/bottomSheet'
import { Checkbox } from '@zet/design-system/checkbox'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B2, SH2 } from '@zet/design-system/typography'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  useGetApplicationPartnerId,
  useIsApplicationV2,
} from '../../api/applicationDetails/hook'
import { useApiAction } from '../../api/useApiAction'
import { brandsI18n } from '../../localization'
import { OTPVerifyScreenTransformer } from '../../transformers/otpScreenTransformer'
import { isPLHLPartner } from '../../utils/plhlPartnerIds'
import PDFViewer from '../common/pdfViewer/index.web'
import Footer from '../footer/footer'
import OtpVerification, { OtpVerificationRef } from './otpVerification'

interface Props {
  maskNumber?: boolean
  hideTnC?: boolean
  hideOTPMessage?: boolean
}

const Otp = (_props: { props: Props }) => {
  const { height } = Dimensions.get('window')
  const { props } = _props
  const navigation = useNavigation()

  const { data, isLoading, hash, stateManagePost, error } = useApiAction()
  const isV2 = useIsApplicationV2(hash)
  const partnerId = useGetApplicationPartnerId(hash)
  const prevState = data?.prevState || ''

  const { tncContent, otpLength, description, retryCount, title, resend } =
    OTPVerifyScreenTransformer.transform({
      ...data,
      maskNumber: props?.maskNumber,
    })

  const {
    showTnC,
    text,
    linkTitle,
    tncURI,
    controlled,
    privacyTitle,
    privacyURI,
  } = tncContent
  const OTPVerificationRef = useRef<OtpVerificationRef>(null)

  const otp = useRef<string>()
  const toast = useToast()

  const [showTnCBottomSheet, setShowTnCBottomSheet] = useState({
    show: false,
    uri: '',
  })
  const [showSpinner, setShowSpinner] = useState(true)

  const validationSchema = z.object({
    consentToTnc: z.boolean().refine((val) => val),
  })

  const {
    control,
    formState: { isValid },
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      consentToTnc: true,
    },
    resolver: zodResolver(validationSchema),
  })

  const onSubmit = ({ otpLength = 6 }: { otpLength: number }) => {
    if (otp.current?.length === otpLength) {
      stateManagePost({
        payload: {
          data: {
            otp: otp.current,
          },
        },
        options: {
          onSuccess: (data = {}) => {
            if (data?.data?.error?.code) {
              if ([8, 3].includes(data?.data?.error?.code))
                OTPVerificationRef?.current?.setTimer(0)
            }
            if (isV2 && data?.data?.data?.error) {
              toast?.show({
                intent: 'error',
                heading: brandsI18n.t('common.incorrectOtp'),
                hideAfter: 3000,
              })
            } else if (data?.data?.error) {
              toast?.show({
                intent: 'error',
                heading: data?.data?.error?.message,
                hideAfter: 3000,
              })
            }
            if (isPLHLPartner({ partnerId }) && data?.data?.error) {
              toast?.show({
                intent: 'error',
                heading:
                  data?.data?.error?.message ??
                  brandsI18n.t('common.SomethingWentWrong'),
                hideAfter: 3000,
              })
            }
          },
        },
      })
    }
  }

  const onChange = (val: string) => {
    otp.current = String(val)
  }

  const openTermsAndConditions = () => {
    setShowTnCBottomSheet({
      show: true,
      uri: tncURI,
    })
    setShowSpinner(true)
  }

  const openPrivacyPolicy = () => {
    setShowTnCBottomSheet({
      show: true,
      uri: privacyURI,
    })
    setShowSpinner(true)
  }

  const onDismiss = () => {
    setShowTnCBottomSheet({
      show: false,
      uri: '',
    })
    setShowSpinner(false)
  }

  const hideSpinnerLoader = () => {
    setShowSpinner(false)
  }

  const goToPrevState = () => {
    navigation.replace('BrandsCC', {
      state: prevState,
      hash,
    })
  }

  return (
    <KeyBoardWrapper
      className="items flex-1 bg-white"
      BottomSheet={
        <>
          <Stack className="m-3">
            <Alert intent="warning">
              You have {retryCount} attempt{retryCount > 1 ? 's' : ''} left for
              OTP verification
            </Alert>
          </Stack>
          {showTnC && !props?.hideTnC && (
            <Controller
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <XStack className="mx-3 my-2">
                  {controlled && (
                    <Stack className="mr-2 mt-1">
                      <Checkbox
                        onChange={(value) => {
                          onChange(value)
                          onBlur()
                        }}
                        accesibilityLabel="consentToTnc"
                        checked={value}
                      />
                    </Stack>
                  )}
                  <B2>
                    {text}
                    <SH2
                      className="text-primary-100 font-[Graphie-SemiBold]"
                      onPress={openTermsAndConditions}
                    >
                      {linkTitle}{' '}
                    </SH2>
                    <SH2
                      className="text-primary-100 font-[Graphie-SemiBold]"
                      onPress={openPrivacyPolicy}
                    >
                      {privacyTitle}{' '}
                    </SH2>
                  </B2>
                </XStack>
              )}
              name="consentToTnc"
            />
          )}
          <Footer
            step={data?.layout?.step}
            buttonProps={{
              text: 'Submit',
              onPress: () => onSubmit({ otpLength }),
              loading: isLoading,
              disabled: !isValid && controlled,
            }}
          />
        </>
      }
    >
      {prevState ? (
        <Stack className="mx-3 mt-3 items-start">
          <Pressable
            className="rounded bg-[#F5F7FB] p-1"
            onPress={goToPrevState}
          >
            <Icon type="ArrowLeft" color="#2F3440" />
          </Pressable>
        </Stack>
      ) : null}
      <OtpVerification
        onChange={onChange}
        isError={
          Boolean(error && !props?.hideOTPMessage) &&
          !isPLHLPartner({ partnerId })
        }
        description={description}
        otpLength={otpLength}
        hash={hash}
        title={title}
        ref={OTPVerificationRef}
        resendCount={resend?.resendCount}
      />
      {showTnC !== false && (
        <BottomSheet visible={showTnCBottomSheet.show} onDismiss={onDismiss}>
          <Stack style={{ height: height / 0.9 }}>
            {showSpinner && <ActivityIndicator />}
            <PDFViewer
              uri={showTnCBottomSheet?.uri}
              onLoad={hideSpinnerLoader}
            />
          </Stack>
        </BottomSheet>
      )}
    </KeyBoardWrapper>
  )
}

export default Otp
